import { getDb } from "./firebase-lazy";
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
} from "firebase/firestore";

/**
 * 피드백 리포트 도착 알림 생성
 * 학생에게 커넥톤 내 알림만 전송 (이메일 불필요)
 */
export async function notifyStudentReportReady(
  studentId: string,
  studentEmail: string = "", // 선택사항 (사용하지 않음, 호환성을 위해 유지)
  reportId: string,
  songTitle: string,
  proName: string
): Promise<{ success: boolean; error?: string }> {
  void studentEmail;

  try {
    const db = getDb();

    // Firestore notifications 컬렉션에 알림 추가
    await addDoc(collection(db, "notifications"), {
      userId: studentId,
      type: "feedback_report_ready",
      title: "🎵 프로 피드백이 도착했습니다!",
      message: `${proName} 전문가가 "${songTitle}" 곡에 대한 피드백을 작성했습니다.`,
      link: `/profile/reports`,
      reportId,
      isRead: false,
      createdAt: serverTimestamp(),
    });

    // TODO: 이메일 알림 (선택사항)
    // await sendEmailNotification(studentEmail, songTitle, proName);

    console.log("✅ 학생에게 리포트 도착 알림 전송 완료");

    return { success: true };
  } catch (error) {
    console.error("❌ 알림 생성 실패:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "알림 생성 실패",
    };
  }
}

/**
 * 학생이 피드백 요청 시 전문가에게 알림
 */
export async function notifyProNewRequest(
  proId: string,
  studentName: string,
  songTitle: string,
  genre: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const db = getDb();

    await addDoc(collection(db, "notifications"), {
      userId: proId,
      type: "new_feedback_request",
      title: "📝 새로운 피드백 요청이 도착했습니다",
      message: `${studentName}님이 "${songTitle}" (${genre}) 곡에 대한 피드백을 요청했습니다.`,
      link: `/instructor/dashboard?tab=feedback`,
      isRead: false,
      createdAt: serverTimestamp(),
    });

    console.log("✅ 전문가에게 새 요청 알림 전송 완료");

    return { success: true };
  } catch (error) {
    console.error("❌ 알림 생성 실패:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "알림 생성 실패",
    };
  }
}

interface AdminNotificationPayload {
  requestId: string;
  studentId: string;
  studentName: string;
  category: string;
  feedbackOption: number;
  hasVideo: boolean;
  hasPdf: boolean;
}

export async function notifyAdminsNewFeedbackRequest(
  payload: AdminNotificationPayload
): Promise<{ success: boolean; error?: string }> {
  try {
    const db = getDb();

    const usersRef = collection(db, "users");

    const [roleAdminsSnap, flagAdminsSnap] = await Promise.all([
      getDocs(query(usersRef, where("role", "==", "admin"))),
      getDocs(query(usersRef, where("isAdmin", "==", true))),
    ]);

    const adminIds = new Set<string>();

    roleAdminsSnap.forEach(doc => adminIds.add(doc.id));
    flagAdminsSnap.forEach(doc => adminIds.add(doc.id));

    if (adminIds.size === 0) {
      console.warn("⚠️ 관리자 계정을 찾을 수 없어 알림을 전송하지 못했습니다.");
      return { success: false, error: "관리자 계정을 찾을 수 없습니다." };
    }

    const notificationsRef = collection(db, "notifications");
    const notificationData = Array.from(adminIds).map(adminId =>
      addDoc(notificationsRef, {
        userId: adminId,
        type: "feedback_request_admin",
        title: "새 피드백 요청이 접수되었습니다",
        message: `${payload.studentName}님의 피드백 요청이 접수되었습니다. (${payload.category})`,
        data: {
          requestId: payload.requestId,
          studentId: payload.studentId,
          feedbackOption: payload.feedbackOption,
          hasVideo: payload.hasVideo,
          hasPdf: payload.hasPdf,
        },
        link: `/connect-admin/assign-feedback?requestId=${payload.requestId}`,
        isRead: false,
        priority: "high",
        createdAt: serverTimestamp(),
      })
    );

    await Promise.all(notificationData);

    console.log(
      `✅ 관리자 ${adminIds.size}명에게 새 피드백 요청 알림 전송 완료`
    );

    return { success: true };
  } catch (error) {
    console.error("❌ 관리자 알림 생성 실패:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "관리자 알림 생성 실패",
    };
  }
}

/**
 * 프로 배정 알림 생성
 * 사용자에게 프로가 배정되었음을 알림
 */
export async function notifyStudentProAssigned(
  studentId: string,
  proId: string,
  proName: string,
  requestId: string,
  songTitle?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const db = getDb();

    await addDoc(collection(db, "notifications"), {
      userId: studentId,
      type: "pro_assigned",
      title: "✨ 전문가가 배정되었습니다!",
      message: `${proName} 전문가가 "${songTitle || "요청하신 곡"}" 피드백을 맡게 되었습니다.`,
      data: {
        proId,
        proName,
        requestId,
        songTitle,
      },
      link: `/profile/expert-analysis`,
      isRead: false,
      createdAt: serverTimestamp(),
    });

    console.log("✅ 사용자에게 프로 배정 알림 전송 완료");

    return { success: true };
  } catch (error) {
    console.error("❌ 프로 배정 알림 생성 실패:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "알림 생성 실패",
    };
  }
}

/**
 * 작업 착수 알림
 * 프로가 마감기한을 설정하여 작업을 시작했음을 알림
 */
export async function notifyStudentWorkStarted(
  studentId: string,
  proName: string,
  songTitle: string,
  deadline: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const db = getDb();

    // 마감일을 한국 시간으로 포맷
    const deadlineDate = new Date(deadline);
    const formattedDeadline = deadlineDate.toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    await addDoc(collection(db, "notifications"), {
      userId: studentId,
      type: "work_started",
      title: "🎬 프로가 작업을 시작했습니다!",
      message: `${proName} 전문가가 "${songTitle}" 피드백 작업을 시작했습니다. ${formattedDeadline}까지 받아보실 수 있습니다.`,
      data: {
        proName,
        songTitle,
        deadline,
        expectedDate: formattedDeadline,
      },
      link: `/profile/reports`,
      isRead: false,
      createdAt: serverTimestamp(),
    });

    console.log("✅ 학생에게 작업 착수 알림 전송 완료");

    return { success: true };
  } catch (error) {
    console.error("❌ 작업 착수 알림 생성 실패:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "알림 생성 실패",
    };
  }
}
